import type {
  ClientAllowance,
  OpenHold,
  PlayerBucket,
} from "@pax/api-contract/accounting/v1alpha1";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  focusHaloFlush,
  HapticSurface,
  panelCard,
  panelMotion,
  press,
} from "@pax/ui";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Flag, Plus, X } from "lucide-react";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useSession } from "@/features/auth/queries";
import { AgentFeedback } from "@/features/play/components/AgentFeedback";
import { requestErrorMessage } from "@/lib/api";
import { formatTimeAgo } from "@/lib/format";
import {
  creditsFromRaw,
  creditsOf,
  formatCredits,
  formatMoney,
  type PackOffer,
  paymentMethodLabel,
} from "../format";
import { journalLabel, readableCode } from "../journals";
import {
  balanceQuery,
  type CreditHistoryLine,
  cachedCredits,
  creditHistoryQuery,
  offersQuery,
  openHoldsQuery,
  refreshTopUp,
  spendableCredits,
  topUpSettingsQuery,
} from "../queries";
import { markCheckoutStarted } from "../resume-after-checkout";
import { useTopUp } from "../top-up";
import { useStartCheckout } from "../use-start-checkout";
import { Coin } from "./Coin";
import { ConfirmPurchaseDialog } from "./ConfirmPurchaseDialog";
import { PaymentsClosedNote } from "./PaymentsClosedNote";

const bucketLabels: Record<PlayerBucket, string> = {
  daily: "Daily credits",
  moderation: "Moderation credits",
  signup: "Signup bonus",
  sponsorship: "Sponsorship",
  commission: "Creator earnings",
  compensation: "Compensation",
  subscription: "Subscription",
  oneTimePurchase: "Purchased credits",
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const MINUTE_MS = 60 * 1000;

/** Older changes start loading this far before the foot of the list scrolls into view. */
const OLDER_PAGE_MARGIN = "0px 0px 240px 0px";

const thisWeekFormat = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  hour: "numeric",
  minute: "2-digit",
});

const olderFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const topUpTime = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

/** When the next daily top-up comes, at most a day off, or that the next sign-in claims it. */
function dailyTopUpNote(nextClaimableAt: string | null): string {
  const next = nextClaimableAt ? new Date(nextClaimableAt) : null;
  if (!next || next.getTime() <= Date.now()) return "Top-up at next sign-in";
  const today = next.toDateString() === new Date().toDateString();
  return `Next top-up ${today ? "today" : "tomorrow"} ${topUpTime.format(next)}`;
}

/**
 * What is left of the free allowance for direct AI calls outside games, which applies until the
 * player buys credits.
 */
function clientAllowanceNote(allowance: ClientAllowance): string {
  if (BigInt(allowance.remainingRaw) > 0n) {
    return `${formatCredits(creditsFromRaw(allowance.remainingRaw), { precise: true })} of free direct AI use left today`;
  }
  return allowance.resetsAt
    ? `Free direct AI use frees up ${topUpTime.format(new Date(allowance.resetsAt))}`
    : "Free direct AI use is spent for today";
}

/** Whether a model call charge fell on this player or was split with others. */
function splitOf(payerCount: number): string {
  if (payerCount === 1) return "you";
  return `shared with ${payerCount - 1} ${payerCount === 2 ? "other" : "others"}`;
}

function formatWhen(recordedAt: string): string {
  const when = new Date(recordedAt);
  return (Date.now() - when.getTime() < WEEK_MS ? thisWeekFormat : olderFormat).format(when);
}

/** Re-renders every minute, so times written relative to now stay true. */
function useMinuteTick() {
  const [, tick] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    const interval = setInterval(tick, MINUTE_MS);
    return () => clearInterval(interval);
  }, []);
}

/** Watches the foot of the list, so the next older page loads as the reader scrolls down to it. */
function useHistoryFoot({
  list,
  enabled,
  pageCount,
  onReach,
}: {
  list: RefObject<HTMLElement | null>;
  enabled: boolean;
  pageCount: number;
  onReach: () => void;
}) {
  const foot = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = foot.current;
    if (!element || !enabled) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onReach();
      },
      { root: list.current, rootMargin: OLDER_PAGE_MARGIN },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [enabled, list, onReach, pageCount]);

  return foot;
}

/** Signed out there are no credits to show, so there is no panel. */
export function CreditsPane({ onClose }: { onClose?: () => void }) {
  const { data: session } = useSession();
  if (!session?.user) return null;
  return (
    <CreditsBody
      onClose={onClose}
      userId={session.user.id}
      hasAccount={!session.user.isAnonymous}
    />
  );
}

function CreditsBody({
  onClose,
  userId,
  hasAccount,
}: {
  onClose?: () => void;
  userId: string;
  /** Guests can neither buy credits nor claim the daily refresh. */
  hasAccount: boolean;
}) {
  const queryClient = useQueryClient();
  const { openTopUp } = useTopUp();
  const { data: balance } = useQuery(balanceQuery(userId));
  const { data: openHolds } = useQuery(openHoldsQuery(userId));
  const { data: offers = [] } = useQuery({ ...offersQuery, enabled: hasAccount });
  const { data: topUpSettings } = useQuery({ ...topUpSettingsQuery, enabled: hasAccount });
  const packs = offers.filter((offer): offer is PackOffer => offer.kind === "pack");
  const paymentsOpen = topUpSettings?.paymentsClosedReason === null;
  const savedMethodLabel = topUpSettings?.paymentMethod
    ? paymentMethodLabel(topUpSettings.paymentMethod)
    : null;
  const [toppingUp, setToppingUp] = useState(false);
  const [confirming, setConfirming] = useState<PackOffer | null>(null);
  const { startCheckout, checkout } = useStartCheckout();
  const {
    data: history,
    isPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery(creditHistoryQuery(userId));
  const historyElement = useRef<HTMLElement>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [feedbackLine, setFeedbackLine] = useState<CreditHistoryLine | null>(null);
  useMinuteTick();
  const shownHolds = openHolds?.openHolds.filter(hold => new Date(hold.expiresAt) > new Date());

  const loadOlder = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const historyFoot = useHistoryFoot({
    list: historyElement,
    enabled: hasNextPage && !isFetchingNextPage && !isFetchNextPageError,
    pageCount: history?.length ?? 0,
    onReach: loadOlder,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-w-0 shrink-0 items-center gap-2.5 px-3 pt-3 pb-4">
        <Coin className="size-9" />
        <div className="min-w-0 flex-1">
          <p className="text-lg leading-tight font-medium tabular-nums">
            {balance === undefined ? "–" : formatCredits(spendableCredits(balance))}
          </p>
          <p className="text-xs text-muted-foreground">Credits you can spend</p>
          {balance && hasAccount && (
            <p className="truncate text-[11px] leading-4 text-muted-foreground">
              {dailyTopUpNote(balance.refresh.nextClaimableAt)}
            </p>
          )}
          {balance && !balance.clientAllowance.unlimited && (
            <p className="truncate text-[11px] leading-4 text-muted-foreground">
              {clientAllowanceNote(balance.clientAllowance)}
            </p>
          )}
        </div>
        {packs.length > 0 && paymentsOpen && (
          <button
            type="button"
            onClick={() => setToppingUp(open => !open)}
            aria-label="Top up credits"
            aria-expanded={toppingUp}
            className={cn(
              "relative grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground aria-expanded:bg-foreground/[0.07] aria-expanded:text-foreground",
              panelMotion,
              press,
              focusHaloFlush,
            )}
          >
            <Plus aria-hidden className="size-4" />
            <HapticSurface />
          </button>
        )}
        {/* Where this is a sheet, the corner is the first place a thumb goes
            to put it away, so it has to find a close there. */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close credits"
            className={cn(
              "relative grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground",
              panelMotion,
              press,
              focusHaloFlush,
            )}
          >
            <X aria-hidden className="size-4" />
            <HapticSurface />
          </button>
        )}
      </div>

      {hasAccount && packs.length > 0 && (
        <div className="flex shrink-0 flex-col gap-2 px-3 pb-3">
          {topUpSettings?.paymentsClosedReason && (
            <PaymentsClosedNote reason={topUpSettings.paymentsClosedReason} />
          )}
          {paymentsOpen && toppingUp && (
            <>
              <ul aria-label="Top up" className={`flex flex-col gap-0.5 p-1 ${panelCard}`}>
                {packs.map(offer => (
                  <li key={offer.offerId}>
                    <button
                      type="button"
                      disabled={checkout.isPending}
                      onClick={() => {
                        if (savedMethodLabel) {
                          setConfirming(offer);
                          return;
                        }
                        startCheckout(offer.offerId);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-sm hover:bg-foreground/[0.07] disabled:opacity-60",
                        focusHaloFlush,
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Coin className="size-4 shrink-0" />
                        <span className="truncate">{creditsOf(offer)} credits</span>
                      </span>
                      <span className="shrink-0 font-medium tabular-nums">
                        {checkout.isPending && checkout.variables?.offerId === offer.offerId
                          ? "Opening…"
                          : formatMoney(offer.priceCents)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {checkout.isError && (
                <p role="alert" className="px-1 text-xs text-destructive">
                  {requestErrorMessage(checkout.error)}
                </p>
              )}
            </>
          )}
          {paymentsOpen && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                openTopUp();
                onClose?.();
              }}
            >
              See more
            </Button>
          )}
        </div>
      )}

      <ConfirmPurchaseDialog
        offer={confirming}
        paymentMethodLabel={savedMethodLabel ?? ""}
        onPaid={() => {
          markCheckoutStarted(cachedCredits(queryClient));
          void refreshTopUp(queryClient);
        }}
        onClose={() => setConfirming(null)}
        onUseDifferentPaymentMethod={() => {
          const offer = confirming;
          setConfirming(null);
          if (offer) startCheckout(offer.offerId);
        }}
      />

      <section
        ref={historyElement}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll region takes focus so the keyboard can scroll it.
        tabIndex={0}
        aria-label="Credit history"
        className={cn("scroll-fade flex flex-1 flex-col overflow-y-auto px-3 pb-3", focusHaloFlush)}
      >
        {shownHolds && shownHolds.length > 0 && (
          <ul aria-label="Open holds" className={`mb-2 flex flex-col gap-0.5 p-1 ${panelCard}`}>
            {shownHolds.map(hold => (
              <li
                key={hold.holdId}
                className="flex items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-sm"
              >
                <HistorySummary
                  title="In progress"
                  detail={
                    <>
                      <time dateTime={hold.startedAt}>{formatTimeAgo(hold.startedAt)}</time>
                      {` · ${holdContext(hold)}`}
                    </>
                  }
                  amount={formatCredits(creditsFromRaw(hold.estimateRaw), { precise: true })}
                  amountClassName="text-muted-foreground"
                />
              </li>
            ))}
          </ul>
        )}
        {isPending ? null : history?.length ? (
          <ul className={`flex flex-col gap-0.5 p-1 ${panelCard}`}>
            {history.map(line => (
              <HistoryRow
                key={line.entryId}
                line={line}
                open={openEntryId === line.entryId}
                toggle={() => setOpenEntryId(open => (open === line.entryId ? null : line.entryId))}
                giveFeedback={() => setFeedbackLine(line)}
              />
            ))}
          </ul>
        ) : (
          <p className={`px-3 py-3 text-sm text-muted-foreground ${panelCard}`}>
            No credit changes yet.
          </p>
        )}
        {history?.length || hasNextPage ? (
          <p
            role="status"
            aria-live="polite"
            className="px-3 pt-2 text-center text-xs text-muted-foreground"
          >
            {isFetchingNextPage ? (
              "Loading earlier changes…"
            ) : isFetchNextPageError ? (
              <>
                Earlier changes failed to load.{" "}
                <button
                  type="button"
                  onClick={() => void fetchNextPage()}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Retry
                </button>
              </>
            ) : hasNextPage ? (
              "\u00a0"
            ) : (
              "Start of history"
            )}
          </p>
        ) : null}
        <div ref={historyFoot} aria-hidden />
      </section>
      <FeedbackDialog line={feedbackLine} close={() => setFeedbackLine(null)} />
    </div>
  );
}

/** The game, preset, or kind a hold was placed for. */
function holdContext(hold: OpenHold): string {
  return hold.gameTitle ?? hold.presetTitle ?? readableCode(hold.kind);
}

/** Label, when, and amount on one history row. */
function HistorySummary({
  title,
  detail,
  amount,
  amountClassName,
}: {
  title: string;
  detail: ReactNode;
  amount: string;
  amountClassName: string;
}) {
  return (
    <>
      <span className="flex min-w-0 flex-col items-start">
        <span className="max-w-full truncate">{title}</span>
        <span className="max-w-full truncate text-xs text-muted-foreground">{detail}</span>
      </span>
      <span className={cn("shrink-0 font-medium tabular-nums", amountClassName)}>{amount}</span>
    </>
  );
}

/** One credit change, opening in place to show what it was for. */
function HistoryRow({
  line,
  open,
  toggle,
  giveFeedback,
}: {
  line: CreditHistoryLine;
  open: boolean;
  toggle: () => void;
  giveFeedback: () => void;
}) {
  const agent = line.modelCall?.agent;
  const game = line.modelCall?.game;
  const detailsId = `credit-change-${line.entryId}`;
  return (
    <li
      className={cn(
        "rounded-lg text-sm transition-[background-color,margin,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
        open && "my-1 bg-background/70 shadow-sm ring-1 ring-foreground/[0.06]",
      )}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={detailsId}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-left",
          !open && "hover:bg-foreground/[0.07]",
          panelMotion,
          focusHaloFlush,
        )}
      >
        <HistorySummary
          title={agent ? `${agent.name} v${agent.version}` : journalLabel(line.journal)}
          detail={
            <>
              <time dateTime={line.recordedAt}>{formatTimeAgo(line.recordedAt)}</time>
              {game ? ` · ${game.title}` : ""}
            </>
          }
          amount={formatCredits(line.credits, {
            signed: true,
            precise: line.journal === "pendingCreatorReward",
          })}
          amountClassName={line.credits > 0 ? "text-success" : "text-destructive"}
        />
      </button>
      <div
        id={detailsId}
        inert={!open}
        className={cn(
          "grid transition-[grid-template-rows,opacity] ease-[cubic-bezier(0.16,1,0.3,1)]",
          open
            ? "grid-rows-[1fr] opacity-100 duration-300"
            : "grid-rows-[0fr] opacity-0 duration-200",
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              "space-y-2 px-2.5 pt-1 pb-2.5 text-xs transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
              open ? "translate-y-0" : "-translate-y-1",
            )}
          >
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <Detail label="When">{formatWhen(line.recordedAt)}</Detail>
              <Detail label="Kind">{journalLabel(line.journal)}</Detail>
              {agent && <Detail label="Agent">{`${agent.name} v${agent.version}`}</Detail>}
              {game && (
                <Detail label="Game">
                  <Link
                    to="/play/$gameId"
                    params={{ gameId: game.id }}
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    {game.title}
                  </Link>
                </Detail>
              )}
              {line.modelCall && (
                <Detail label="Paid by">{splitOf(line.modelCall.payerCount)}</Detail>
              )}
              {line.buckets.map(({ bucket, credits }) => (
                <Detail key={bucket} label={bucketLabels[bucket]}>
                  <span className="tabular-nums">
                    {formatCredits(credits, {
                      signed: true,
                      precise: line.journal === "pendingCreatorReward",
                    })}
                  </span>
                </Detail>
              ))}
              {line.modelCall && (
                <Detail label="Call">
                  <span className="font-mono text-[11px] break-all">{line.modelCall.callId}</span>
                </Detail>
              )}
            </dl>
            {agent && game && (
              <button
                type="button"
                onClick={giveFeedback}
                className="mx-auto flex items-center gap-1.5 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                <Flag aria-hidden className="size-3 opacity-60" strokeWidth={1.75} />
                Give feedback on {agent.name}
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function FeedbackDialog({ line, close }: { line: CreditHistoryLine | null; close: () => void }) {
  const agent = line?.modelCall?.agent;
  const game = line?.modelCall?.game;
  return (
    <Dialog
      open={Boolean(agent && game)}
      onOpenChange={open => {
        if (!open) close();
      }}
    >
      {agent && game && (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{`${agent.name} v${agent.version}`}</DialogTitle>
            <DialogDescription>In {game.title}</DialogDescription>
          </DialogHeader>
          <AgentFeedback
            key={line?.entryId}
            gameId={game.id}
            versionId={agent.versionId}
            question={`How is ${agent.name} doing?`}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}
